/**
 * * Effect docs: https://enhanced-button.vercel.app/
 */
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-all",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground shadow hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground shadow hover:bg-destructive/90",
				outline: "border border-input bg-background shadow hover:bg-input",
				"primary-outline":
					"border border-primary text-primary hover:bg-primary hover:text-primary-foreground",
				"accent-outline":
					"border border-border text-accent-foreground border hover:bg-accent hover:text-accent-foreground",
				"destructive-outline":
					"border border-destructive bg-background text-destructive hover:bg-destructive hover:text-destructive-foreground",
				secondary:
					"bg-secondary text-secondary-foreground shadow hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				"ghost-destructive": "text-destructive hover:bg-accent",
				"ghost-primary":
					"text-primary hover:bg-primary hover:text-primary-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			effect: {
				expandIcon: "group gap-0 relative",
				ringHover:
					"transition-all duration-300 hover:ring-2 hover:ring-primary/90 hover:ring-offset-2",
				shine:
					"before:animate-shine relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-no-repeat background-position_0s_ease",
				shineHover:
					"relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:transition-[background-position_0s_ease] hover:before:bg-[position:-100%_0,0_0] before:duration-1000",
				gooeyRight:
					"relative z-0 overflow-hidden transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r from-white/40 before:transition-transform before:duration-1000  hover:before:translate-x-[0%] hover:before:translate-y-[0%]",
				gooeyLeft:
					"relative z-0 overflow-hidden transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l from-white/40 after:transition-transform after:duration-1000  hover:after:translate-x-[0%] hover:after:translate-y-[0%]",
				underline:
					"relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300",
				hoverUnderline:
					"relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300",
			},
			size: {
				default: "h-9 px-4 py-2",
				xs: "h-6 px-2 text-xs",
				sm: "h-8 rounded-md px-3 text-xs",
				lg: "h-10 rounded-md px-8",
				xl: "h-12 rounded-md px-10 text-base",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export type ButtonVariant = VariantProps<typeof buttonVariants>;

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		ButtonVariant {
	asChild?: boolean;
}

export type ButtonIconProps =
	| {
			icon: React.ElementType;
			iconPlacement: "left" | "right";
	  }
	| {
			icon?: never;
			iconPlacement?: undefined;
	  };

const Button = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & ButtonIconProps
>(
	(
		{
			className,
			variant,
			effect,
			size,
			icon: Icon,
			iconPlacement,
			asChild = false,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, effect, size, className }))}
				ref={ref}
				{...props}
			>
				{Icon &&
					iconPlacement === "left" &&
					(effect === "expandIcon" ? (
						<div className="w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-100 group-hover:pr-2 group-hover:opacity-100">
							<Icon />
						</div>
					) : (
						<Icon />
					))}
				<Slottable>{props.children}</Slottable>
				{Icon &&
					iconPlacement === "right" &&
					(effect === "expandIcon" ? (
						<div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
							<Icon />
						</div>
					) : (
						<Icon />
					))}
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
